import React, { useState, useEffect } from 'react';
import { Users, Eye, TrendingUp, Clock } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { getVisitorStats, formatVisitorCount, initializeVisitorTracking } from '@/utils/analytics';

const VisitorStatsWidget = ({ showDetailed = false }) => {
  const [stats, setStats] = useState({
    totalVisitors: '0',
    todayVisitors: '0', 
    pageViews: '0',
    lastVisit: null
  });
  
  useEffect(() => {
    // Initialize visitor tracking
    if (initializeVisitorTracking()) {
      setStats(getVisitorStats());
    }
    
    // Update stats periodically
    const interval = setInterval(() => {
      setStats(getVisitorStats());
    }, 30000); // Update every 30 seconds
    
    return () => clearInterval(interval);
  }, []);
  
  if (showDetailed) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4 bg-gradient-to-r from-teal-50 to-cyan-50 border-teal-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-teal-700">
                {formatVisitorCount(stats.totalVisitors)}
              </p>
              <p className="text-sm text-teal-600">Total Visitors</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4 bg-gradient-to-r from-cyan-50 to-teal-50 border-cyan-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-cyan-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-cyan-700">
                {formatVisitorCount(stats.todayVisitors)}
              </p>
              <p className="text-sm text-cyan-600">Today's Visitors</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <Eye className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-700">
                {formatVisitorCount(stats.pageViews)}
              </p>
              <p className="text-sm text-green-600">Page Views</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-lg font-bold text-purple-700">Active</p>
              <p className="text-sm text-purple-600">Status</p>
            </div>
          </div>
        </Card>
      </div>
    );
  }
  
  // Compact version for dashboard
  return (
    <Card className="p-4 bg-gradient-to-r from-teal-50 to-cyan-50 border-teal-200">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
            <Users className="w-4 h-4 text-teal-600" />
          </div>
          <div>
            <p className="font-semibold text-gray-800">Site Visitors</p>
            <p className="text-sm text-gray-600">Real-time tracking</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-teal-700">
            {formatVisitorCount(stats.totalVisitors)}
          </p>
          <p className="text-xs text-teal-600">
            +{stats.todayVisitors} today
          </p>
        </div>
      </div>
    </Card>
  );
};

export default VisitorStatsWidget;